import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { generateDefaultValues, Schema, schema } from "./schema";
import { generateCreateFacilityReportPayload } from "./utils";
import { TimeUnitSelect } from "../../../../AnalysisCollectionsComponents/CollectionChartArea/LineChart/TimeUnitSelect";
import { HookFormCheckbox } from "../../../../HookForm/Checkbox";
import { HookForm } from "../../../../HookForm/HookForm";
import { HookFormLanguageSelect } from "../../../../HookForm/LanguageSelect";
import { LocationSelect } from "../../../../HookForm/LocationSelect";
import { HookFormTimespanSelect } from "../../../../HookForm/TimespanSelect";

import "./styles.scss";

interface ICreateNewFacilityReportFormProps {
  handleClose: () => void;
  handleSubmit: (payload: any) => void;
}

export const CreateNewFacilityReportForm: React.FC<
  ICreateNewFacilityReportFormProps
> = (props) => {
  const { handleClose, handleSubmit } = props;

  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityReportsPage.modalCreateNew.${key}`);

  const defaultValues = generateDefaultValues();

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (data: Schema) => {
    const payload = generateCreateFacilityReportPayload(data);
    handleSubmit(payload);
  };

  return (
    <HookForm
      className="create-new-facility-report-form"
      contentClassName="create-new-facility-report-form-content"
      form={form}
      onSubmit={onSubmit}
      submitButtonLabel={t("create")}
      onCancel={handleClose}
      showCancelButton
    >
      <div className="section">
        <h3 className="section-header">{t("sections.facilities.label")}</h3>
        <Controller
          control={form.control}
          name="locations"
          render={({ field }) => {
            return (
              <LocationSelect
                selectedLocations={field.value}
                setSelectedLocations={field.onChange}
              />
            );
          }}
        />
      </div>
      <div className="section">
        <h3 className="section-header">{t("sections.timespan.label")}</h3>
        <HookFormTimespanSelect name="timespan" />
      </div>
      <div className="section two-columns">
        <HookFormCheckbox
          labelClassName="section-header"
          label={t("fields.facility_snapshot.is_enabled.label")}
          name="options.facility_snapshot.is_enabled"
        />
      </div>
      <div className="options-sections-container">
        <div className="section">
          <HookFormCheckbox
            labelClassName="section-header"
            label={t("fields.search_keywords.is_enabled.label")}
            name="options.search_keywords.is_enabled"
          />
          <HookFormCheckbox
            className="sub-item"
            labelClassName="sub-item-label"
            label={t("fields.search_keywords.ai_summary_enabled.label")}
            name="options.search_keywords.ai_summary_enabled"
            disabled={!form.watch("options.search_keywords.is_enabled")}
          />
        </div>
        <div className="section">
          <HookFormCheckbox
            labelClassName="section-header"
            label={t("fields.website.is_enabled.label")}
            name="options.website.is_enabled"
          />
          <Controller
            control={form.control}
            name="options.website.time_unit"
            render={({ field }) => {
              return (
                <TimeUnitSelect
                  timeUnit={field.value}
                  setTimeUnit={field.onChange}
                  disabled={!form.watch("options.website.is_enabled")}
                />
              );
            }}
          />
          <HookFormCheckbox
            className="sub-item"
            labelClassName="sub-item-label"
            label={t("fields.website.ai_summary_enabled.label")}
            name="options.website.ai_summary_enabled"
            disabled={!form.watch("options.website.is_enabled")}
          />
        </div>
        <div className="section">
          <HookFormCheckbox
            labelClassName="section-header"
            label={t("fields.calls.is_enabled.label")}
            name="options.calls.is_enabled"
          />
          <Controller
            control={form.control}
            name="options.calls.time_unit"
            render={({ field }) => {
              return (
                <TimeUnitSelect
                  timeUnit={field.value}
                  setTimeUnit={field.onChange}
                  disabled={!form.watch("options.calls.is_enabled")}
                />
              );
            }}
          />
          <HookFormCheckbox
            className="sub-item"
            labelClassName="sub-item-label"
            label={t("fields.calls.ai_summary_enabled.label")}
            name="options.calls.ai_summary_enabled"
            disabled={!form.watch("options.calls.is_enabled")}
          />
        </div>
        <div className="section">
          <HookFormCheckbox
            labelClassName="section-header"
            label={t("fields.route_search.is_enabled.label")}
            name="options.route_search.is_enabled"
          />
          <Controller
            control={form.control}
            name="options.route_search.time_unit"
            render={({ field }) => {
              return (
                <TimeUnitSelect
                  timeUnit={field.value}
                  setTimeUnit={field.onChange}
                  disabled={!form.watch("options.route_search.is_enabled")}
                />
              );
            }}
          />
          <HookFormCheckbox
            className="sub-item"
            labelClassName="sub-item-label"
            label={t("fields.route_search.ai_summary_enabled.label")}
            name="options.route_search.ai_summary_enabled"
            disabled={!form.watch("options.route_search.is_enabled")}
          />
        </div>
        <div className="section">
          <HookFormCheckbox
            labelClassName="section-header"
            label={t("fields.impressions.is_enabled.label")}
            name="options.impressions.is_enabled"
          />
          <Controller
            control={form.control}
            name="options.impressions.time_unit"
            render={({ field }) => {
              return (
                <TimeUnitSelect
                  timeUnit={field.value}
                  setTimeUnit={field.onChange}
                  disabled={!form.watch("options.impressions.is_enabled")}
                />
              );
            }}
          />
          <HookFormCheckbox
            className="sub-item"
            labelClassName="sub-item-label"
            label={t("fields.impressions.ai_summary_enabled.label")}
            name="options.impressions.ai_summary_enabled"
            disabled={!form.watch("options.impressions.is_enabled")}
          />
        </div>
        <div className="section two-columns">
          <HookFormCheckbox
            labelClassName="section-header"
            label={t("fields.reviews.is_enabled.label")}
            name="options.reviews.is_enabled"
          />
          <HookFormLanguageSelect
            className="sub-item"
            name="options.reviews.languages"
            label={t("fields.reviews.languages.label")}
            disabled={!form.watch("options.reviews.is_enabled")}
          />
        </div>
      </div>
      <div className="section two-columns">
        <HookFormCheckbox
          labelClassName="section-header"
          label={t("fields.overall_ai_summary.is_enabled.label")}
          name="options.overall_ai_summary.is_enabled"
        />
      </div>
    </HookForm>
  );
};
